import { styled } from '@chronosphereio/chrono-ui';
import { ListLegendItem, ListLegendItemProps } from './ListLegendItem';
import { isLegendItemSelected, LegendItem, SelectedLegendItemState } from './legend-model';

export interface CompactListLegendProps {
  width: number;
  items: LegendItem[];
  selectedItems: SelectedLegendItemState;
  onLegendItemClick: ListLegendItemProps['onClick'];
  onLegendCheckboxClick: ListLegendItemProps['onCheckboxChange'];
  onItemMouseOver: ListLegendItemProps['onMouseOver'];
  onItemMouseOut: ListLegendItemProps['onMouseOut'];
  disableListLegendItemTruncation: ListLegendItemProps['disableListLegendItemTruncation'];
}

/**
 * CompactListLegend is default and used when legend items need to show side by side
 * which corresponds to when legend.position is `bottom` with a relatively small
 * number of items. The `StackedListLegend` is used for cases with large numbers of items
 * because it is virtualized.
 */
export function CompactListLegend({
  items,
  selectedItems,
  onLegendItemClick,
  onLegendCheckboxClick,
  onItemMouseOver,
  onItemMouseOut,
  disableListLegendItemTruncation,
}: CompactListLegendProps) {
  return (
    <CompactListLegendList>
      {items.map((item, index) => (
        <CompactListLegendItem
          key={item.id}
          item={item}
          index={index}
          isSelected={isLegendItemSelected(item, selectedItems)}
          onMouseOver={onItemMouseOver}
          onMouseOut={onItemMouseOut}
          onClick={onLegendItemClick}
          onCheckboxChange={onLegendCheckboxClick}
          disableListLegendItemTruncation={disableListLegendItemTruncation}
        />
      ))}
    </CompactListLegendList>
  );
}

const CompactListLegendList = styled('ul')(({ theme }) => {
  return {
    width: '100%',
    height: '100%',
    overflowY: 'auto',
    margin: 0,
    padding: theme.spacing(0, 1, 0, 0),
  };
});

const CompactListLegendItem = styled(ListLegendItem)(({ theme }) => {
  const rightMargin = theme.spacing(0.5);

  return {
    float: 'left',
    paddingRight: theme.spacing(0.5),
    marginRight: rightMargin,
    // Allows series with "short names" (i.e. shorter than a single row)
    // to appear side-by-side in a row
    width: 'auto',
    // Allows series with "long names" (i.e. longer than a single row)
    // to be truncated with ellipsis. Calc is used to avoid triggering a scrollbar
    // with the right margin.
    maxWidth: `calc(100% - ${rightMargin})`,
  };
});
